import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  ProductMediaDetail,
  ProductMediaKind,
  ProductMediaSummaryItem,
} from "@school-sales-ai/shared";
import {
  addProductMediaFileApi,
  getProductMediaApi,
  getProductMediaSummaryApi,
  removeProductMediaFileApi,
  reorderProductGalleryApi,
  setProductMediaDescriptionApi,
} from "@/lib/api";

const SUMMARY_KEY = ["product-media", "summary"] as const;
const detailKey = (productId: string) => ["product-media", productId] as const;

/** One request for the whole catalog; every row reads its own entry from it. */
export function useProductMediaSummary(): ReturnType<
  typeof useQuery<ProductMediaSummaryItem[]>
> {
  return useQuery<ProductMediaSummaryItem[]>({
    queryKey: SUMMARY_KEY,
    queryFn: getProductMediaSummaryApi,
  });
}

export function useProductMedia(
  productId: string | null,
): ReturnType<typeof useQuery<ProductMediaDetail>> {
  return useQuery<ProductMediaDetail>({
    queryKey: detailKey(productId ?? ""),
    queryFn: () => getProductMediaApi(productId ?? ""),
    enabled: productId !== null,
  });
}

/**
 * Every mutation answers with the product's fresh detail, which replaces the
 * cached one directly (no refetch), and invalidates the summary so the row's
 * icons follow.
 */
function useProductMediaMutation<TArgs>(
  productId: string,
  mutationFn: (args: TArgs) => Promise<ProductMediaDetail>,
  fallbackError: string,
): ReturnType<typeof useMutation<ProductMediaDetail, Error, TArgs>> {
  const queryClient = useQueryClient();
  return useMutation<ProductMediaDetail, Error, TArgs>({
    mutationFn,
    onSuccess: (detail) => {
      queryClient.setQueryData(detailKey(productId), detail);
      void queryClient.invalidateQueries({ queryKey: SUMMARY_KEY });
    },
    onError: (error) => {
      toast.error(error.message || fallbackError);
    },
  });
}

export function useAddProductMediaFile(productId: string) {
  return useProductMediaMutation<{ kind: ProductMediaKind; file: File }>(
    productId,
    ({ kind, file }) => addProductMediaFileApi(productId, kind, file),
    "No se pudo subir el archivo.",
  );
}

export function useRemoveProductMediaFile(productId: string) {
  return useProductMediaMutation<string>(
    productId,
    (fileId) => removeProductMediaFileApi(fileId),
    "No se pudo quitar el archivo.",
  );
}

export function useSetProductMediaDescription(productId: string) {
  return useProductMediaMutation<{ kind: ProductMediaKind; description: string }>(
    productId,
    ({ kind, description }) => setProductMediaDescriptionApi(productId, kind, description),
    "No se pudo guardar la descripción.",
  );
}

export function useReorderProductGallery(productId: string) {
  return useProductMediaMutation<string[]>(
    productId,
    (fileIds) => reorderProductGalleryApi(productId, fileIds),
    "No se pudo cambiar el orden.",
  );
}
